import {DelayParameter} from '@frontapp/workflows/rules/parameters';
import {isEmpty} from 'lodash';
import {ellipsis} from 'polished';
import React, {CSSProperties, FC, PropsWithChildren} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {defaultCompanyName} from '../../../../../libs/shared/core/src/models/api/company/companyModel';
import {InboxModel} from '../../../../../libs/shared/core/src/models/api/inboxModel';
import {TeamModel} from '../../../../../libs/shared/core/src/models/api/teamModel';
import {makeStrictEnumRuntimeType} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {TeamAvatar} from '../../../../../libs/shared/ui/src/components/avatar/teamAvatar';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {ColorDot} from '../../../../../libs/shared/ui/src/components/utility/colorDot';
import {renderInlineList} from '../../../../../libs/shared/ui/src/helpers/listHelpers';
import {newPalette, NewPaletteColorsEnum} from '../../../../../libs/shared/ui/src/styles/colorStyles';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fonts, fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis as mixinsEllipsis, flexCenter} from '../../../../../libs/shared/ui/src/styles/mixins';
import SlaTimer from '../../../../assets/images/slaTimer.svg';
import {delayToDayHHMM} from '../../../../settings/editors/commons/paramModel';

/*
 * Props.
 */

interface OnboardingInboxIllustrationProps {
  inboxAnimation?: boolean;
  companyName: string;

  team: Pick<TeamModel, 'name' | 'color'>;
  inbox: Pick<InboxModel, 'name' | 'color'>;

  feature: OnboardingInboxVisualFeature | undefined;

  slaRuleSlaReplyTimeGoal: DelayParameter | undefined;

  tagRuleKeywords: ReadonlyArray<string> | undefined;
}

export enum OnboardingInboxVisualFeature {
  SLA_RULE = 'sla_rule',
  TAG_RULE = 'tag_rule',
  ASSIGN_RULE = 'assign_rule',
}

/*
 * Inbox animation.
 */

export enum InboxAnimationStep {
  INITIAL_PAUSE = 'initial_pause',
  REVEAL_SHARED_INBOX = 'reveal_shared_inbox',
  REVEAL_SHARED_INBOX_PAUSE = 'reveal_shared_inbox_pause',
  HIDE_SHARED_INBOX_FLOURISH = 'hide_shared_inbox_flourish',
  ASSIGN_CONVERSATION = 'assign_conversation',
  ASSIGN_CONVERSATION_PAUSE = 'assign_conversation_pause',
  HIDE_ASSIGNED_CONVERSATION = 'hide_assigned_conversation',
  TAG_CONVERSATION_1 = 'tag_conversation_1',
  TAG_CONVERSATION_2 = 'tag_conversation_2',
  TAG_CONVERSATION_3 = 'tag_conversation_3',
  FINAL_PAUSE = 'final_pause',
}

const RInboxAnimationStep = makeStrictEnumRuntimeType(InboxAnimationStep);

const InboxAnimationStepDurationMs: Record<InboxAnimationStep, number> = {
  [InboxAnimationStep.INITIAL_PAUSE]: 1100,
  [InboxAnimationStep.REVEAL_SHARED_INBOX]: 300,
  [InboxAnimationStep.REVEAL_SHARED_INBOX_PAUSE]: 1700,
  [InboxAnimationStep.HIDE_SHARED_INBOX_FLOURISH]: 300,
  [InboxAnimationStep.ASSIGN_CONVERSATION]: 300,
  [InboxAnimationStep.ASSIGN_CONVERSATION_PAUSE]: 1000,
  [InboxAnimationStep.HIDE_ASSIGNED_CONVERSATION]: 400,
  [InboxAnimationStep.TAG_CONVERSATION_1]: 300,
  [InboxAnimationStep.TAG_CONVERSATION_2]: 300,
  [InboxAnimationStep.TAG_CONVERSATION_3]: 300,
  [InboxAnimationStep.FINAL_PAUSE]: 3000,
};

export const INBOX_ANIMATION_DURATION_MS = Object.values(InboxAnimationStepDurationMs).reduce(
  (acc, duration) => acc + duration,
  0,
);

export function computeInboxAnimationStep(step: InboxAnimationStep) {
  const steps = Object.values(InboxAnimationStep).filter(RInboxAnimationStep.is);
  const previousSteps = steps.slice(0, steps.indexOf(step));
  const elapsedTime = previousSteps.reduce(
    (acc, previousStep) => acc + InboxAnimationStepDurationMs[previousStep],
    InboxAnimationStepDurationMs[step],
  );

  return `${(elapsedTime / INBOX_ANIMATION_DURATION_MS) * 100}%`;
}

export function finalInboxAnimationStep() {
  return `100%`;
}

export enum TagAnimationName {
  TAG_ANIMATION_1 = 'tagAnimation1',
  TAG_ANIMATION_2 = 'tagAnimation2',
  TAG_ANIMATION_3 = 'tagAnimation3',
}

/*
 * Constants.
 */

export const ILLUSTRATION_PANEL_WIDTH = 754;

/*
 * Styles.
 */

const StyledPanel = styled.div`
  font-family: ${fonts.brand};
  width: ${ILLUSTRATION_PANEL_WIDTH}px;
  height: 481px;
  overflow: hidden;
  border-radius: 12px 0 0 12px;

  box-shadow: 0 0 20px 0 rgba(0, 27, 56, 0.1);
`;

const StyledP0 = styled.div`
  width: 100%;
  background: ${(p) => p.theme.greys.black};

  display: flex;
  gap: 10px;

  padding: 8px 14px;
`;

const StyledP0IconWrapper = styled.div`
  ${flexCenter};
  justify-content: center;
  height: 26px;
  width: 26px;

  border-radius: 8px;
  background: ${(p) => p.theme.alphas.white30};
  color: ${(p) => p.theme.greys.white};
`;

const StyledMain = styled.div`
  display: flex;
  height: 100%;
`;

const StyledP1 = styled.div`
  display: flex;
  flex-direction: column;

  width: 210px;
  padding: 14px;
  flex-shrink: 0;
  height: 100%;
  background-color: ${(p) => p.theme.background};
  gap: 12px;
`;

const StyledP2 = styled.div`
  width: 190px;
  flex-shrink: 0;

  display: flex;
  flex-direction: column;
  padding: 14px 14px 0 14px;
  overflow: hidden;
`;

const StyledSkeleton = styled.div`
  background-color: ${(p) => p.theme.alphas.black20};
  border-radius: 100px;
  height: 16px;

  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;

  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
`;

const p2PlaceholderHeight = 52;

const StyledP2Placeholder = styled(StyledSkeleton)`
  ${flexCenter};

  background-color: ${(p) => p.theme.greys.white};

  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.semibold};

  width: 100%;
  height: ${p2PlaceholderHeight}px;
  border-radius: 8px;
  flex-shrink: 0;

  margin-bottom: 8px;
`;

const StyledSlaTimer = styled.img`
  height: 16px;
`;

const StyledAnimatedP2Item = styled(StyledP2Placeholder)`
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  animation-name: p2ItemAnimation;
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes p2ItemAnimation {
    ${computeInboxAnimationStep(InboxAnimationStep.ASSIGN_CONVERSATION_PAUSE)} {
      height: ${p2PlaceholderHeight}px;
      margin-bottom: 8px;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_ASSIGNED_CONVERSATION)} {
      height: 0;
      margin-bottom: 0;
    }

    ${finalInboxAnimationStep()} {
      height: 0;
      margin-bottom: 0;
    }
  }
`;

const StyledAnimatedP2ItemChildren = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;

  width: 100%;
  height: 100%;

  color: var(--color);
  background-color: var(--background-color);
  opacity: 0;

  animation-name: p2ChildrenAnimation;
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes p2ChildrenAnimation {
    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_SHARED_INBOX_FLOURISH)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.ASSIGN_CONVERSATION)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }
`;

const StyledP3 = styled.div`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 0;
  padding: 14px 14px 14px 0;
`;

const StyledP3Conversation = styled.div`
  margin-top: 14px;

  width: 492px;
  height: 251px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;

  border-radius: 8px;
  background: ${(p) => p.theme.background};
`;

const StyledMessageHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 8px;
`;

const StyledLineWrapper = styled.div`
  ${flexCenter};
  gap: 6px;
`;

const StyledDot = styled(StyledSkeleton)`
  width: 8px;
  height: 8px;
  padding: 4px;
`;

const StyledInboxLine = styled(StyledLineWrapper)`
  background-color: ${(p) => p.theme.alphas.gray10};
  height: 30px;
  padding: 7px 7px 7px 56px;
  border-radius: 8px;
  // Yes, yes, this is cheating.
  margin: -7px -10px;

  color: ${(p) => p.theme.greys.shade80};
  font-size: ${fontSizes.medium};
  font-weight: ${fontWeights.bold};

  > :first-child {
    flex-shrink: 0;
  }

  > :last-child {
    ${ellipsis()};
  }
`;

const StyledTeamLine = styled(StyledLineWrapper)`
  margin-top: 14px;
  color: ${(p) => p.theme.greys.shade70};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  gap: 3px;
`;

const StyledChevronDownIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledChevronExpandIcon = styled(Icon)`
  transform: scaleY(-1);
  margin-top: 2px;
`;

interface StyledSharedInboxWrapperProps {
  $inboxAnimation: boolean;
}

const StyledSharedInboxWrapper = styled.div<StyledSharedInboxWrapperProps>`
  display: flex;
  flex-direction: column;
  gap: 12px;

  opacity: ${(p) => (p.$inboxAnimation ? '0' : '1')};

  animation-name: ${(p) => (p.$inboxAnimation ? 'sharedInboxAnimation' : 'none')};
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes sharedInboxAnimation {
    ${computeInboxAnimationStep(InboxAnimationStep.INITIAL_PAUSE)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.REVEAL_SHARED_INBOX)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }
`;

interface StyledTagWrapperProps {
  $isSlaTimer: boolean;
}

const StyledTagWrapper = styled.div<StyledTagWrapperProps>`
  ${flexCenter};
  padding: 4px;
  gap: 4px;
  width: min-content;

  margin-left: auto;
  margin-right: 4px;

  border-radius: ${(p) => (p.$isSlaTimer ? 12 : 4)}px;

  background-color: var(--background-color);
  color: var(--color);

  opacity: 0;

  animation-name: var(--tag-animation-name);
  animation-duration: ${INBOX_ANIMATION_DURATION_MS}ms;
  animation-iteration-count: infinite;
  animation-timing-function: linear;
  animation-direction: normal;

  @keyframes ${TagAnimationName.TAG_ANIMATION_1} {
    ${computeInboxAnimationStep(InboxAnimationStep.HIDE_ASSIGNED_CONVERSATION)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_1)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }

  @keyframes ${TagAnimationName.TAG_ANIMATION_2} {
    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_1)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_2)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }

  @keyframes ${TagAnimationName.TAG_ANIMATION_3} {
    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_2)} {
      opacity: 0;
    }

    ${computeInboxAnimationStep(InboxAnimationStep.TAG_CONVERSATION_3)} {
      opacity: 1;
    }

    ${finalInboxAnimationStep()} {
      opacity: 1;
    }
  }
`;

/*
 * SLA rule styles.
 */

const StyledSlaPill = styled.div`
  border-radius: 16px;
  background: ${(p) => p.theme.greys.white};
  box-shadow: 0px 0px 10px 0px rgba(0, 27, 56, 0.10);
  padding: 4px 8px;
  gap: 4px;
  height 24px;
  margin-left: 16px;
  font-family: Roobert;
  font-size: ${fontSizes.verySmall};
  font-weight: ${fontWeights.semibold};
  line-height: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

const StyledSlaBreachDurationWrapper = styled.div`
  padding: 0 6px;
  background: ${(p) => p.theme.greys.shade30};
  border-radius: 4px;
  height: 16px;
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  justify-content: space-between;
`;

const StyledSlaBreachDurationValue = styled.div`
  display: flex;
  flex-direction: row;
  align-items: baseline;
  gap: 2px;
`;

const StyledSlaBreachDurationPlaceholderSquare = styled.div`
  background: ${(p) => p.theme.greys.shade50};
  border-radius: 2px;
  width: 8px;
  height: 8px;
`;

const StyledSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: 20px;
  gap: 4px;
`;

/*
 * Tag rule styles.
 */

const StyledTagRuleSkeletonPlaceholder = styled.div`
  background: ${(p) => p.theme.greys.shade50};
  border-radius: 2px;
  width: 48px;
  height: 12px;
  margin: 0 2px;
`;

const StyledKeywordsSkeleton = styled(StyledSkeleton)`
  height: 20px;
  flex-shrink: 0;
  padding: 0 6px;
  max-width: 256px;
  justify-content: flex-start;
  font-size: ${fontSizes.verySmall};
`;

const StyledKeywordsSkeletonKeywords = styled.div`
  ${mixinsEllipsis()};
`;

const StyledOrSpan = styled.span`
  color: ${(p) => p.theme.greys.shade70};
`;

/*
 * Assign rule styles.
 */

const StyledAssignRuleP2Placeholder = styled(StyledP2Placeholder)`
  align-items: flex-start;
  justify-content: flex-start;
`;

const StyledAssignRulePill = styled.div`
  margin-top: 6px;
  margin-left: 6px;
  border-radius: 14px;
  width: 28px;
  height: 14px;
  color: ${(p) => p.theme.palette.blue.shade50};
  background: ${(p) => p.theme.palette.blue.shade20};
  font-size: 8px;
  font-weight: ${fontWeights.semibold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

/*
 * Component.
 */

export const OnboardingInboxIllustration: FC<OnboardingInboxIllustrationProps> = ({
  companyName,
  inboxAnimation,
  team,
  inbox,
  feature,
  slaRuleSlaReplyTimeGoal,
  tagRuleKeywords,
}) => (
  <StyledPanel>
    {/* P0 */}
    <StyledP0>
      <StyledP0IconWrapper>
        <Icon name="inboxFilledSmall" />
      </StyledP0IconWrapper>
      <StyledP0IconWrapper />
      <StyledP0IconWrapper />
      <StyledP0IconWrapper />
    </StyledP0>
    <StyledMain>
      {/* P1 */}
      <StyledP1>
        {/* Personal items */}
        <StyledSkeleton style={{width: 96, height: 28, marginBottom: 32}} />
        <StyledSkeleton style={{width: 50}} />
        <StyledLineWrapper>
          <StyledChevronDownIcon name="chevronDownSmall" />
          <StyledSkeleton style={{width: 16}} />
          <StyledSkeleton style={{width: 80}} />
        </StyledLineWrapper>
        <StyledLineWrapper style={{paddingLeft: 46}}>
          <StyledDot />
          <StyledSkeleton style={{width: 80}} />
        </StyledLineWrapper>
        <StyledLineWrapper style={{paddingLeft: 46}}>
          <StyledDot />
          <StyledSkeleton style={{width: 50}} />
        </StyledLineWrapper>

        {/* Team items */}
        <StyledSharedInboxWrapper $inboxAnimation={inboxAnimation ?? false}>
          <StyledTeamLine>
            <span>{team.name}</span>
            <StyledChevronExpandIcon name="chevronExpandTiny" />
          </StyledTeamLine>

          {/* Team shared inboxes */}
          <StyledLineWrapper>
            <StyledChevronDownIcon name="chevronDownSmall" />
            {renderTeamAvatar(companyName, team)}
            <span>Shared inboxes</span>
          </StyledLineWrapper>

          <StyledInboxLine>
            <ColorDot size={8} color={inbox.color} />
            <div>
              {isEmpty(inbox.name) ? (
                <StyledSkeleton style={{width: 80, backgroundColor: 'rgba(0, 0, 0, 0.15)'}} />
              ) : (
                inbox.name
              )}
            </div>
          </StyledInboxLine>
          <StyledLineWrapper style={{paddingLeft: 46}}>
            <StyledDot />
            <StyledSkeleton style={{width: 50}} />
          </StyledLineWrapper>
          <StyledLineWrapper style={{paddingLeft: 46}}>
            <StyledDot />
            <StyledSkeleton style={{width: 50}} />
          </StyledLineWrapper>
        </StyledSharedInboxWrapper>
      </StyledP1>
      {/* P2 */}
      <StyledP2>
        {/* Tag & item animations */}
        {inboxAnimation && (
          <>
            <AnimatedP2Item color={NewPaletteColorsEnum.BLUE}>
              <Icon name="assignFilled" />
            </AnimatedP2Item>

            <StyledP2Placeholder>
              <TagIllustration
                color={NewPaletteColorsEnum.GREEN}
                tagAnimationName={TagAnimationName.TAG_ANIMATION_1}
              >
                <StyledSkeleton style={{width: 35, height: 12, borderRadius: 4}} />
              </TagIllustration>
            </StyledP2Placeholder>

            <StyledP2Placeholder>
              <TagIllustration
                color={NewPaletteColorsEnum.BLUE}
                tagAnimationName={TagAnimationName.TAG_ANIMATION_2}
              >
                <StyledSkeleton style={{width: 35, height: 12, borderRadius: 4}} />
              </TagIllustration>
            </StyledP2Placeholder>

            <StyledP2Placeholder>
              <TagIllustration
                color={NewPaletteColorsEnum.ORANGE}
                tagAnimationName={TagAnimationName.TAG_ANIMATION_3}
                isSlaTimer
              >
                <StyledSkeleton style={{width: 16, height: 12, borderRadius: 4}} />
              </TagIllustration>
            </StyledP2Placeholder>

            <StyledP2Placeholder />
          </>
        )}
        {!inboxAnimation && (
          <>
            {feature === OnboardingInboxVisualFeature.ASSIGN_RULE ? (
              <StyledAssignRuleP2Placeholder>
                <StyledAssignRulePill>NEW</StyledAssignRulePill>
              </StyledAssignRuleP2Placeholder>
            ) : (
              <StyledP2Placeholder />
            )}
            <StyledP2Placeholder />
            <StyledP2Placeholder />
            <StyledP2Placeholder />
          </>
        )}
      </StyledP2>
      {/* P3 */}
      <StyledP3>
        <StyledSkeleton style={{width: 120, height: 20}} />
        <StyledP3Conversation>
          <StyledMessageHeader>
            <StyledSkeleton style={{height: 30, width: 30}} />
            {feature === OnboardingInboxVisualFeature.SLA_RULE && (
              <StyledSlaPill>
                Unreplied after {renderSlaBreachDuration(slaRuleSlaReplyTimeGoal)}
              </StyledSlaPill>
            )}
          </StyledMessageHeader>
          {feature === OnboardingInboxVisualFeature.TAG_RULE ? (
            <StyledSkeletonWrapper>
              <StyledSkeleton style={{width: 60, flexShrink: 0}} />
              <StyledKeywordsSkeleton>
                {tagRuleKeywords && (tagRuleKeywords?.length ?? 0) > 0 ? (
                  <StyledKeywordsSkeletonKeywords>
                    {renderInlineList(
                      tagRuleKeywords.map((keyword) => <>&ldquo;{keyword}&rdquo;</>),
                      ',',
                      <StyledOrSpan key="array-to-separated-list-or">
                        {' '}
                        <Trans id="array-to-separated-list-or">or</Trans>{' '}
                      </StyledOrSpan>,
                    )}
                  </StyledKeywordsSkeletonKeywords>
                ) : (
                  <>
                    &ldquo;
                    <StyledTagRuleSkeletonPlaceholder />
                    &rdquo;
                  </>
                )}
              </StyledKeywordsSkeleton>
              <StyledSkeleton style={{width: 392, flexShrink: 1}} />
            </StyledSkeletonWrapper>
          ) : (
            <StyledSkeletonWrapper>
              <StyledSkeleton style={{width: 460}} />
            </StyledSkeletonWrapper>
          )}
          <StyledSkeletonWrapper>
            <StyledSkeleton style={{width: 460}} />
          </StyledSkeletonWrapper>
          <StyledSkeletonWrapper>
            <StyledSkeleton style={{width: 180}} />
          </StyledSkeletonWrapper>
          <StyledSkeletonWrapper>
            <StyledSkeleton style={{width: 172, marginTop: 48}} />
          </StyledSkeletonWrapper>
        </StyledP3Conversation>
      </StyledP3>
    </StyledMain>
  </StyledPanel>
);

interface TagIllustrationProps extends PropsWithChildren {
  color: NewPaletteColorsEnum;
  tagAnimationName: TagAnimationName;
  isSlaTimer?: boolean;
}

const TagIllustration: FC<TagIllustrationProps> = ({color, children, tagAnimationName, isSlaTimer}) => (
  <StyledTagWrapper
    $isSlaTimer={isSlaTimer ?? false}
    style={{
      ...makeTagAnimationNameVariable(tagAnimationName),
      ...makeNewPaletteColorVariables(color),
    }}
  >
    {isSlaTimer ? <StyledSlaTimer src={SlaTimer} alt="SLA timer" /> : <Icon name="tagFilledSmall" />}
    {children}
  </StyledTagWrapper>
);

interface AnimatedP2ItemProps extends PropsWithChildren {
  color: NewPaletteColorsEnum;
}

const AnimatedP2Item: FC<AnimatedP2ItemProps> = ({color, children}) => (
  <StyledAnimatedP2Item
    style={{
      ...makeNewPaletteColorVariables(color),
    }}
  >
    <StyledAnimatedP2ItemChildren>{children}</StyledAnimatedP2ItemChildren>
  </StyledAnimatedP2Item>
);

function renderTeamAvatar(companyName: string, team: Pick<TeamModel, 'color' | 'name'>) {
  return (
    <TeamAvatar
      team={{
        name: team.name === defaultCompanyName ? companyName : team.name,
        color: team.color,
      }}
      size={VisualSizesEnum.SMALL}
    />
  );
}

function makeNewPaletteColorVariables(color: NewPaletteColorsEnum) {
  return {
    '--color': newPalette[color].lowlight,
    '--background-color': newPalette[color].highlight,
  } as CSSProperties;
}

function makeTagAnimationNameVariable(tagAnimationName: TagAnimationName) {
  return {
    '--tag-animation-name': `${tagAnimationName}`,
  } as CSSProperties;
}

function renderSlaBreachDuration(slaRuleSlaReplyTimeGoal: DelayParameter | undefined) {
  const renderValue = (value: number | undefined, unit: string) => {
    if (value === undefined) {
      return (
        <StyledSlaBreachDurationValue>
          <StyledSlaBreachDurationPlaceholderSquare />
          {unit}
        </StyledSlaBreachDurationValue>
      );
    }

    if (value === 0) {
      return undefined;
    }

    return (
      <StyledSlaBreachDurationValue>
        {value}
        {unit}
      </StyledSlaBreachDurationValue>
    );
  };

  const hhMm = slaRuleSlaReplyTimeGoal && delayToDayHHMM(slaRuleSlaReplyTimeGoal, 'hours');

  if ((hhMm?.hh ?? 0) === 0 && (hhMm?.mm ?? 0) === 0) {
    return (
      <StyledSlaBreachDurationWrapper>
        <StyledSlaBreachDurationValue>
          <StyledSlaBreachDurationPlaceholderSquare />H
        </StyledSlaBreachDurationValue>
        <StyledSlaBreachDurationValue>
          <StyledSlaBreachDurationPlaceholderSquare />M
        </StyledSlaBreachDurationValue>
      </StyledSlaBreachDurationWrapper>
    );
  }

  return (
    <StyledSlaBreachDurationWrapper>
      {renderValue(hhMm?.hh, 'H')}
      {renderValue(hhMm?.mm, 'M')}
    </StyledSlaBreachDurationWrapper>
  );
}
